import { ExpensePaymentHistory } from '../../types/supabase'
import { formatDateInTimeZone } from '../../lib/formatDateInTimeZone'
import { formatCurrency } from '../../lib/formatCurrency'
import { present } from '../../lib/util/present'
import { Tooltip } from './tooltip'

import './bankChargeChip.scss'

type PlaidPayment = ExpensePaymentHistory['payments'][number]

/**
 * A single credit-card charge line for a Plaid-sourced payment. Shows the source
 * bank (Plaid gives us the institution, not the card network) with the account's
 * last-4, plus the charge amount and date. Falls back to a generic "Card" label
 * for older entries written before the bank was denormalized onto the payment.
 */
export function BankChargeChip({ payment }: { payment: PlaidPayment }) {
  const bank = present(payment.institution_name) ? payment.institution_name : 'Card'
  const mask = present(payment.account_mask) ? payment.account_mask : null

  return (
    <Tooltip tooltip="Charge imported from your connected bank or card account.">
      <span className="bank-charge-chip">
        <i className="material-icons bank-charge-chip__icon" aria-hidden="true">credit_card</i>
        <span className="bank-charge-chip__bank">{bank}</span>
        {mask && <span className="bank-charge-chip__mask">•• {mask}</span>}
        <span className="bank-charge-chip__amount">{formatCurrency(payment.amount)}</span>
        <span className="bank-charge-chip__date">
          {formatDateInTimeZone(payment.date, { format: 'MM/dd/yyyy' })}
        </span>
      </span>
    </Tooltip>
  )
}

/** Whether a payment_history entry originated from a Plaid/card charge. */
export function isPlaidCharge(payment: PlaidPayment): boolean {
  // `source` didn't exist on older entries; a plaid_transaction_id alone still
  // identifies them as plaid-sourced.
  return payment.source === 'plaid' || present(payment.plaid_transaction_id)
}
